import Icon from '@ant-design/icons';
import Modal from 'antd/es/modal/Modal';
import classNames from 'classnames';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import CloseSvg from '@/components/Icon/svgs/close.svg';
import LoadMore from '@/components/LoadMore';
import NoteItem from '@/components/Note/NoteItem';
import ChatNoteCard from '@/components/Note/components/ChatNoteCard';
import styles from '@/components/Note/index.less';
import Waterfall from '@/components/Waterfall';
import DotsLoading from '@/components/dots-loading';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import type { MemoryListItem } from '@/service/note';
import useBiogStore from '@/store/useBiogStore';
import type { Biog, BiogWord } from '@/types/visualization';
import SimplyMD from '@/ui/components/memory-detail/components/attachments/components/SimplyMD';
import { ROUTER_PATH, generateParams } from '@/utils/router';

import style from './index.less';

interface IProps {
  keywords: Biog[] | undefined;
  open: boolean;
  chosenBiog: BiogWord | undefined;
  handleCancel: () => void;
}

const Detail = (props: IProps) => {
  const { keywords, open, chosenBiog, handleCancel } = props;
  const [layoutSize] = useStorage('layoutSize');
  const [inMobile, setInMobile] = useState(layoutSize === LAYOUT_SIZE.SMALL);
  const router = useRouter();
  const searchParams = useSearchParams();
  const shadeDetail = useBiogStore((state) => state.shadeDetail);
  const detailLoading = useBiogStore((state) => state.detailLoading);
  const updateShadeDetail = useBiogStore((state) => state.updateShadeDetail);
  const paramBiog = searchParams.get('chosenBiog');
  const { t } = useTranslation();

  const id = useMemo(() => {
    return chosenBiog?.id || paramBiog || '';
  }, [chosenBiog, paramBiog]);

  useEffect(() => {
    setInMobile(layoutSize === LAYOUT_SIZE.SMALL);
  }, [layoutSize]);

  useEffect(() => {
    if (id && open) {
      updateShadeDetail({ shadeId: id }, true);
    }
  }, [id, open]);

  const loadMore = () => {
    if (!id) return Promise.reject(new Error(t('error.oops')));

    return updateShadeDetail({ shadeId: id }, false);
  };

  const renderMemoryItem = (item: MemoryListItem) => {
    if (item.type === 'note') {
      const note = item.note;

      return (
        <NoteItem
          key={note.noteId}
          detail={note}
          noteClassName="!shadow-[0_4px_12px_0_rgba(194,158,107,0.2)]"
          onClick={() => {
            if (note.noteId) {
              sessionStorage.setItem(`openMemoryNoteId`, note.noteId);

              router.push(
                generateParams(
                  inMobile
                    ? ROUTER_PATH.personalAIMemoryDetailMobile
                    : ROUTER_PATH.personalAIMemoryDetail,
                  {
                    chosenBiog: chosenBiog?.id || paramBiog || ''
                  }
                )
              );
            }
          }}
          origin={'chat'}
          showTime
        />
      );
    }

    if (item.type === 'chat') {
      const chat = item.chat;

      return <ChatNoteCard key={chat.id} detail={chat} showTime />;
    }

    return null;
  };

  return (
    <Modal
      className={style.modal}
      closable={false}
      footer={null}
      mask={false}
      onCancel={handleCancel}
      open={open}
    >
      <div
        className={classNames(
          'max-w-[930px] w-[930px] flex flex-col gap-8 py-7 px-[60px]',
          inMobile ? '!p-0' : ''
        )}
        id="bio-detail-scroll-container"
      >
        <div className="flex justify-between items-center">
          <div className="font-[Calistoga-Regular] text-2xl leading-8">
            {paramBiog
              ? // 使用Icon
                keywords?.map((item) => {
                  if (item.id == paramBiog) {
                    return item.icon;
                  }

                  return '';
                })
              : chosenBiog?.icon}{' '}
            {chosenBiog?.word || shadeDetail.word}
          </div>
          <div
            className="bg-[rgba(254,253,255,0.5)] w-9 h-9 rounded-[18px] text-base flex items-center justify-center cursor-pointer hover:bg-[rgba(254,253,255,0.8)] transition-all"
            onClick={() => {
              handleCancel();
              router.replace(inMobile ? ROUTER_PATH.personalAIMobile : ROUTER_PATH.personalAI);
            }}
          >
            <Icon component={CloseSvg} />
          </div>
        </div>
        {detailLoading ? (
          <DotsLoading className="mt-24" />
        ) : (
          <div className="flex flex-col gap-2">
            {shadeDetail.content ? (
              <SimplyMD
                className="text-[#643E54] text-base leading-[30px] font-[PTSerif] !mb-4"
                content={shadeDetail.content}
              />
            ) : null}
            <div className="text-[#643E54] text-base leading-6 font-[PTSerif]">
              {shadeDetail.total ? t('common.relatedMemories', { number: shadeDetail.total }) : ''}
            </div>
            <div className="w-full h-full flex flex-col gap-20 pb-20">
              {shadeDetail.list?.length ? (
                <>
                  <Waterfall className={styles.noteContainer} cols={inMobile ? 1 : 2} gap={24}>
                    {shadeDetail.list.map((item) => renderMemoryItem(item))}
                  </Waterfall>
                  {(shadeDetail.total || 0) > shadeDetail.list.length ? (
                    <LoadMore
                      className={'mt-5'}
                      loadMore={loadMore}
                      scrollContainerId="bio-detail-scroll-container"
                    />
                  ) : null}
                </>
              ) : (
                <div className="mt-5 flex items-center flex-col py-[120px] bg-[var(--background-light-gray)] rounded-[12px]">
                  <img
                    alt="empty"
                    className="h-[180px] mb-10"
                    src="https://frontdoorcdn.mindverse.ai/mindos-resource/front-img/crm/20231101169882725146420231101-162603.png"
                  />
                  <div className="text-sm leading-7 font-medium text-primary-black flex flex-col items-center">
                    {`Feed Second Me with ideas, links, takeaways, life moments...`}
                  </div>
                  <div className="text-sm leading-7 font-medium text-primary-black flex flex-col items-center">
                    {`Second Me will help you organize and inspire you.`}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default Detail;
